<template>
  <div>
    <header class="topbar">
      <div>
        <div class="topTitle">{{ pageTitle }}</div>
      </div>
      <div class="topRight">
        <el-button size="small" native-type="button" v-has="'btn_add'" @click="showStaffCreateModal">
          <span class="topActionPlus">+</span> 添加招聘HR
        </el-button>
      </div>
    </header>

    <CommonFilterTableCard
      title="📞 招聘HR管理"
      :items="recruiters"
      :page.sync="page.pageNo"
      :page-size.sync="page.pageSize"
      :use-server-pagination="true"
      :total="recruiterTotal"
      :loading="tableLoading"
      :filter-schema="hrFilterSchema"
      :filter-values.sync="hrFilters"
      empty-text="暂无招聘HR"
      :columns="hrColumns"
      @page-change="fetchRecruiterList"
      @filter-change="onRecruiterFilterChange"
    >
      <template #col-email="{ row }">
        {{ row.email || "—" }}
      </template>

      <template #col-status="{ row }">
        <span v-if="row && row.status != null && String(row.status).trim() !== ''" :class="['status-badge', staffStatusClass(normalizeStaffStatus(row.status))]">
          {{ normalizeStaffStatus(row.status) }}
        </span>
        <span v-else></span>
      </template>

      <template #col-actions="{ row }">
        <el-button size="small" native-type="button" v-has="'btn_edit'" class="btnLink small" @click="showStaffEditModal(row)">编辑</el-button>
        <el-button size="small" native-type="button" v-has="'btn_delete'" class="btnLink danger small" @click="deleteHr(row)">删除</el-button>
      </template>
    </CommonFilterTableCard>

    <!-- 添加/编辑弹窗 -->
    <el-dialog
      :title="staffModalTitle"
      :visible.sync="showStaffModal"
      width="520px"
      :close-on-click-modal="false"
      @close="closeStaffModal"
    >
      <el-form ref="staffFormRef" :model="staffForm" :rules="staffFormRules" label-position="top" class="staff-modal-form">
        <el-form-item prop="oaUserId">
          <template slot="label">
            <span>选择人员</span>
          </template>
          <el-select
            v-model="staffForm.oaUserId"
            filterable
            remote
            :remote-method="remoteOaUserSearch"
            :loading="oaUserLoading"
            :disabled="staffModalMode === 'edit'"
            :clearable="staffModalMode === 'create'"
            placeholder="请选择"
            style="width: 100%"
            @change="onOaUserSelect"
            @clear="onOaUserClear"
          >
            <el-option
              v-for="u in oaUserOptions"
              :key="u.userId"
              :label="u.name + '（' + u.workNo + '）'"
              :value="u.userId"
            />
          </el-select>
        </el-form-item>

        <el-form-item prop="phone">
          <template slot="label">
            <span>电话</span>
          </template>
          <el-input
            v-model="staffForm.phone"
            maxlength="11"
            :disabled="staffModalMode === 'create'"
            placeholder="请输入"
          />
        </el-form-item>

        <el-form-item prop="email">
          <template slot="label">
            <span>邮箱</span>
          </template>
          <el-input
            v-model="staffForm.email"
            maxlength="50"
            :disabled="staffModalMode === 'create'"
            placeholder="请输入"
          />
        </el-form-item>
      </el-form>

      <span slot="footer" class="dialog-footer">
        <el-button @click="closeStaffModal">取消</el-button>
        <el-button type="primary" @click="confirmStaffModal">确认</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import CommonFilterTableCard from "../components/CommonFilterTableCard.vue";
import { addRecruiter, editRecruiter, listRecruiters, removeRecruiter, searchOaUsers } from "@/api/modules/users/recruiter";
import { isEmail, isMobile11 } from "@/utils/validator";
import { STAFF_STATUS } from "@/constants/staff";

export default {
  name: "HrManagePage",
  components: { CommonFilterTableCard },
  data() {
    const validatePhone = (rule, value, callback) => {
      const phone = String(value || "").trim();
      if (!phone) {
        callback(new Error("请输入电话"));
        return;
      }
      if (!isMobile11(phone)) {
        callback(new Error("请输入正确的手机号（11位）"));
        return;
      }
      callback();
    };
    const validateEmail = (rule, value, callback) => {
      const email = String(value || "").trim();
      if (!email) {
        callback(new Error("请输入邮箱"));
        return;
      }
      if (!isEmail(email)) {
        callback(new Error("请输入正确的邮箱"));
        return;
      }
      callback();
    };
    return {
      page: { pageNo: 1, pageSize: 10 },
      hrFilters: { search: "", status: "all" },
      hrFilterSchema: [
        {
          label: "员工状态",
          field: "status",
          type: "select",
          options: [
            { label: "全部", value: "all" },
            { label: "正式", value: "Formal" },
            { label: "试用期", value: "Try" },
            { label: "实习期", value: "Intern" },
            { label: "待离职", value: "WaitLeave" },
            { label: "已离职", value: "Leaved" },
          ],
        },
        {
          label: "姓名/工号",
          field: "search",
          type: "input",
          placeholder: "请输入",
        }
      ],
      tableLoading: false,
      recruiterTotal: 0,
      recruiterRows: [],
      showStaffModal: false,
      staffModalMode: "create", // create | edit
      staffEditingId: null,
      oaUserOptions: [],
      oaUserLoading: false,
      oaUserQueryTimer: null,
      staffForm: { oaUserId: "", name: "", empId: "", phone: "", email: "", password: "", status: "正式" },
      staffFormRules: {
        oaUserId: [{ required: true, message: "请选择人员", trigger: "change" }],
        phone: [
          { required: true, message: "请输入电话", trigger: ["blur", "change"] },
          { validator: validatePhone, trigger: ["blur", "change"] },
        ],
        email: [
          { required: true, message: "请输入邮箱", trigger: ["blur", "change"] },
          { validator: validateEmail, trigger: ["blur", "change"] },
        ],
      },
    };
  },
  computed: {
    pageTitle() {
      return (this.$route.meta && this.$route.meta.title) || "招聘HR管理";
    },
    pageSub() {
      return (this.$route.meta && this.$route.meta.subTitle) || "";
    },
    hrColumns() {
      return [
        { label: "姓名", prop: "name", minWidth: 100 },
        { label: "工号", prop: "work_no", minWidth: 110 },
        { label: "电话", prop: "phone", minWidth: 120 },
        // { label: "邮箱", slot: "email", minWidth: 180 },
        { label: "员工状态", slot: "status", minWidth: 100 },
        { label: "操作", slot: "actions", minWidth: 140, fixed: "right" },
      ];
    },
    staffStatusOptions() {
      return STAFF_STATUS.options;
    },
    recruiters() {
      return this.recruiterRows;
    },
    staffModalTitle() {
      return this.staffModalMode === "edit" ? "编辑招聘HR" : "添加招聘HR";
    },
  },
  methods: {
    /**
     * 员工状态标准化映射
     * Formal-正式, Try-试用期, Leaved-已离职, WaitLeave-待离职
     * @param {string} status 状态英文码或中文文案
     * @returns {string} 标准中文状态文案
     */
    normalizeStaffStatus(status) {
      const map = {
        Formal: "正式",
        Try: "试用期",
        Intern: "实习期",
        Leaved: "已离职",
        WaitLeave: "待离职",
        Waitleave: "待离职",
      };
      const raw = String(status || "").trim();
      return map[raw] || raw || "—";
    },
    /**
     * 员工状态映射为接口入参
     * @param {string} status 中文状态
     * @returns {string} 接口英文状态
     */
    mapStatusToApi(status) {
      const map = {
        正式: "Formal",
        试用期: "Try",
        实习期: "Intern",
        已离职: "Leaved",
        待离职: "WaitLeave",
      };
      const raw = String(status || "").trim();
      return map[raw] || raw;
    },
    async fetchRecruiterList(pageNum) {
      this.tableLoading = true;
      try {
        if (pageNum != null) {
          this.page.pageNo = parseInt(pageNum, 10) || 1;
        }
        this.page.pageSize = parseInt(this.page.pageSize, 10) || 10;
        const statusVal = this.hrFilters && this.hrFilters.status;
        const params = {
          page_num: this.page.pageNo,
          page_size: this.page.pageSize,
          query: (this.hrFilters.search || "").trim(),
          ...(statusVal && statusVal !== "all" ? { status: statusVal } : {}),
        };
        const res = await listRecruiters(params);

        // 兼容：后端可能直接返回数组，也可能返回 {rows,total}
        if (Array.isArray(res)) {
          this.recruiterRows = res;
          this.recruiterTotal = res.length;
        } else {
          this.recruiterRows = Array.isArray(res && res.rows) ? res.rows : [];
          this.recruiterTotal = typeof (res && res.total) === "number" ? res.total : 0;
        }
      } catch (e) {
        this.recruiterRows = [];
        this.recruiterTotal = 0;
      } finally {
        this.tableLoading = false;
      }
    },
    onRecruiterFilterChange() {
      this.page.pageNo = 1;
      this.fetchRecruiterList(1);
    },
    staffStatusClass(status) {
      if (status === "正式") return "status-official";
      if (status === "试用期") return "status-probation";
      if (status === "实习期") return "status-probation";
      if (status === "已离职") return "status-resigned";
      if (status === "待离职") return "status-pending";
      return "status-resigned";
    },
    // el-select 远程搜索：对输入进行防抖后再请求 /oa/search/oaUser
    remoteOaUserSearch(query) {
      const q = (query && String(query).trim()) || "00";
      if (this.oaUserQueryTimer) clearTimeout(this.oaUserQueryTimer);
      this.oaUserQueryTimer = setTimeout(() => {
        this.fetchOaUserOptions(q);
      }, 300);
    },
    async fetchOaUserOptions(query = "00") {
      this.oaUserLoading = true;
      try {
        const res = await searchOaUsers({ query: query || "00" });
        const list = Array.isArray(res) ? res : [];

        // 编辑态：如果当前选择不在后端截取结果中，补一个 option 以保证下拉框显示正确
        const currentId = this.staffForm && this.staffForm.oaUserId;
        if (currentId && !list.some((u) => String(u.userId) === String(currentId))) {
          list.unshift({
            userId: currentId,
            workNo: this.staffForm.empId,
            name: this.staffForm.name,
            phoneNum: this.staffForm.phone,
            email: this.staffForm.email,
            status: this.staffForm.status,
            passWord: this.staffForm.password || null,
            roleType: null,
          });
        }

        this.oaUserOptions = list;
      } catch (e) {
        this.oaUserOptions = [];
      } finally {
        this.oaUserLoading = false;
      }
    },
    onOaUserSelect() {
      const selected = this.oaUserOptions.find((u) => String(u.userId) === String(this.staffForm.oaUserId));
      if (!selected) return;

      // 由 OA 人员信息自动填充
      this.staffForm.name = selected.name || "";
      this.staffForm.empId = selected.workNo || "";
      this.staffForm.phone = selected.phoneNum || "";
      this.staffForm.email = selected.email || "";

      const normalized = this.normalizeStaffStatus(selected.status);
      this.staffForm.status = normalized === "—" ? "正式" : normalized;

      // 添加时允许用 OA 提供的初始密码覆盖
      if (this.staffModalMode === "create" && selected.passWord) {
        this.staffForm.password = selected.passWord;
      }

      // 选择人员后主动触发表单校验
      this.$nextTick(() => {
        const formRef = this.$refs.staffFormRef;
        if (!formRef) return;
        ["phone", "email"].forEach((field) => formRef.validateField(field));
      });
    },
    onOaUserClear() {
      // 添加态清除已选人员时，联动清空自动回填字段
      if (this.staffModalMode !== "create") return;
      this.staffForm.oaUserId = "";
      this.staffForm.name = "";
      this.staffForm.empId = "";
      this.staffForm.phone = "";
      this.staffForm.email = "";
    },
    showStaffCreateModal() {
      this.staffModalMode = "create";
      this.staffEditingId = null;
      this.staffForm = {
        oaUserId: "",
        name: "",
        empId: "",
        phone: "",
        email: "",
        password: "123456",
        status: "正式",
      };
      this.showStaffModal = true;
      this.$nextTick(() => {
        if (this.$refs.staffFormRef) this.$refs.staffFormRef.clearValidate();
      });
      this.fetchOaUserOptions("00");
    },
    showStaffEditModal(p) {
      // 每次进入编辑都清空上次下拉缓存，避免旧数据串到本次编辑
      this.oaUserOptions = [];
      if (this.oaUserQueryTimer) {
        clearTimeout(this.oaUserQueryTimer);
        this.oaUserQueryTimer = null;
      }

      this.staffModalMode = "edit";
      this.staffEditingId = p.userid || p.id;
      const normalizedStatus = this.normalizeStaffStatus(p.status);
      this.staffForm = {
        oaUserId: p.userid || p.id,
        name: p.name || "",
        empId: p.work_no || p.empId || "",
        phone: p.phone || "",
        email: p.email || "",
        password: "",
        status: normalizedStatus === "—" ? "正式" : normalizedStatus,
      };
      this.showStaffModal = true;
      this.$nextTick(() => {
        if (this.$refs.staffFormRef) this.$refs.staffFormRef.clearValidate();
      });
      const query = p.work_no || p.empId || "00";
      this.fetchOaUserOptions(query);
    },
    closeStaffModal() {
      this.showStaffModal = false;
      if (this.oaUserQueryTimer) {
        clearTimeout(this.oaUserQueryTimer);
        this.oaUserQueryTimer = null;
      }
    },
    async confirmStaffModal() {
      const phone = (this.staffForm.phone || "").trim();
      const email = (this.staffForm.email || "").trim();
      const valid = await new Promise((resolve) => {
        this.$refs.staffFormRef.validate((ok) => resolve(ok));
      });
      if (!valid) return;

      if (this.staffModalMode === "edit" && this.staffEditingId != null) {
        this.updateRecruiter({ userid: this.staffEditingId, phone, email });
        return;
      }

      // 添加招聘HR：后端已改为只需要 oa 用户 userid
      this.createRecruiter({ userid: this.staffForm.oaUserId });
    },
    async createRecruiter(payload) {
      const params = { userid: payload && payload.userid };
      try {
        await addRecruiter(params);
        this.$message.success("招聘HR添加成功");
        this.closeStaffModal();
        this.page.pageNo = 1;
        await this.fetchRecruiterList(1);
      } catch (e) {}
    },
    async updateRecruiter(payload) {
      // 编辑只允许更新电话/邮箱
      const params = {
        userid: payload && payload.userid,
        phone: payload && payload.phone,
        email: payload && payload.email,
      };
      try {
        await editRecruiter(params);
        this.$message.success("招聘HR信息已更新");
        this.closeStaffModal();
        await this.fetchRecruiterList(this.page.pageNo);
      } catch (e) {}
    },
    deleteHr(p) {
      this.$confirm("招聘HR删除后不可登录", "提示", { type: "warning" })
        .then(() => {
          return this.removeRecruiterById(p && (p.userid || p.id));
        })
        .catch(() => {});
    },
    async removeRecruiterById(userid) {
      if (!userid) return;
      try {
        await removeRecruiter({ userid });
        this.$message.success("招聘HR已移除");
        this.page.pageNo = 1;
        await this.fetchRecruiterList(1);
      } catch (e) {}
    },
  },
  created() {
    this.fetchRecruiterList(this.page.pageNo);
  },
};
</script>

<style scoped>
.topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 8px;
  height: 33px;
}

.topTitle {
  font-size: 18px;
  font-weight: 900;
}

.topSub {
  font-size: 12px;
  color: var(--muted);
  margin-top: 2px;
}

.topRight {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.topActionPlus {
  display: inline-block;
  font-size: 13px;
  font-weight: 400;
  line-height: 1;
  transform: scale(1.35);
  transform-origin: center;
  margin-right: 4px;
}

.staff-modal-form .el-input.field-error .el-input__inner,
.staff-modal-form .el-select.field-error .el-input__inner {
  border-color: #f56c6c !important;
}

/* 弹窗表单间距优化（Element-UI 默认间距较大） */
.staff-modal-form .el-form-item {
  margin-bottom: 10px;
}

.staff-modal-form .el-form-item__label {
  font-size: 14px;
  color: #606266;
  line-height: 1.4;
  margin-bottom: 6px;
  padding-top: 0;
  padding-bottom: 0;
}

/* Scoped 样式里覆盖 Element-UI 子组件：使用 Vue 支持的深度选择器 */
::v-deep .el-dialog__body {
  padding-top: 0;
  padding-bottom: 0;
}

::v-deep .el-form-item__label {
  font-size: 14px;
  color: #606266;
  padding-top: 0;
  padding-bottom: 0;
}

.required-label {
  font-size: 14px;
  color: #606266;
}

</style>

